/**
 * Manage API keys for a specific user (admin only).
 * Fetches keys, masks display, and submits batch updates to the backend.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert,
  Container,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { ApiKeyFormData } from './UserManagement'; // Import form data type
import { ApiKeyResponse } from '../services/api.service'; // Import response type from service

// Helper function to mask API keys
/** Obscure an API key except for the last five characters. */
const maskApiKey = (key: string): string => {
  if (!key || key.length <= 5) {
    // Return asterisks if key is short or empty but not null/undefined
    return key ? '*'.repeat(key.length) : ''; 
  }
  const visiblePart = key.slice(-5);
  const maskedPart = '*'.repeat(key.length - 5);
  return maskedPart + visiblePart;
};

const ManageUserApiKeys: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { getUserApiKeys, updateUserApiKeys, users } = useAuth(); 

  const [apiKeyFormData, setApiKeyFormData] = useState<ApiKeyFormData>({
    neolms: '',
    wela: '',
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingKeys, setIsFetchingKeys] = useState<boolean>(false);

  const numUserId = userId ? parseInt(userId, 10) : undefined;
  const targetUser = users?.find(u => u.id === numUserId);

  // Ref to track the previous getUserApiKeys function reference for debugging
  const prevGetUserApiKeysRef = useRef<typeof getUserApiKeys | undefined>(undefined);

  const fetchKeys = useCallback(async () => {
    if (!numUserId || !getUserApiKeys) return;

    // Debugging: Check if getUserApiKeys reference has changed
    if (prevGetUserApiKeysRef.current && prevGetUserApiKeysRef.current !== getUserApiKeys) {
      console.warn('DEBUG: getUserApiKeys function reference CHANGED between renders!');
    }
    prevGetUserApiKeysRef.current = getUserApiKeys; // Store current reference

    console.log("fetchKeys triggered");
    setIsFetchingKeys(true);
    setError('');
    try {
      const userApiKeys = await getUserApiKeys(numUserId);
      const formattedKeys = userApiKeys.reduce<ApiKeyFormData>(
        (acc, key) => {
          if (key.service_name === 'neolms' || key.service_name === 'wela') {
            acc[key.service_name as keyof ApiKeyFormData] = key.api_key || ''; 
          }
          return acc;
        },
        { neolms: '', wela: '' }
      );
      setApiKeyFormData(formattedKeys);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
      setError('Failed to load API keys. Please try again.');
    } finally {
      console.log("Setting isFetchingKeys to false");
      setIsFetchingKeys(false);
      setInitialLoading(false);
    }
  }, [userId, numUserId, getUserApiKeys]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;    
    setApiKeyFormData((prev: ApiKeyFormData) => ({ 
      ...prev, 
      [name as keyof ApiKeyFormData]: value, 
    })); 
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    
    // Ensure numUserId is valid before proceeding
    if (typeof numUserId !== 'number') {
      setError('Invalid User ID.');
      setIsSaving(false);
      return;
    }

    // Clear previous messages
    setError('');
    setSuccess('');

    try {
      const keysToUpdate = [];
      if (apiKeyFormData.neolms) {
          keysToUpdate.push({ service_name: 'neolms', api_key: apiKeyFormData.neolms });
      }
      if (apiKeyFormData.wela) {
          keysToUpdate.push({ service_name: 'wela', api_key: apiKeyFormData.wela });
      }
      
      if (keysToUpdate.length > 0) {
         await updateUserApiKeys(numUserId, keysToUpdate); 
         setSuccess('API keys updated successfully!');
      } else {
         setSuccess('No changes submitted.');
      }

      setTimeout(() => navigate('/users'), 2000); 

    } catch (err) {
      console.error('Failed to update API keys:', err);
      setError('Failed to update API keys. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/users'); 
  };

  if (isFetchingKeys || initialLoading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Manage API Keys {targetUser ? `for ${targetUser.username}` : `for User ID: ${userId}`}
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="NeoLMS API Key"
              name="neolms"
              value={maskApiKey(apiKeyFormData.neolms)} // Display masked value
              onChange={handleInputChange}
              variant="outlined"
              helperText="Enter the NeoLMS API key. Leave blank to keep the existing key."
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="WELA API Token"
              name="wela"
              value={maskApiKey(apiKeyFormData.wela)} // Display masked value
              onChange={handleInputChange}
              variant="outlined"
               helperText="Enter the WELA API Token (e.g., 65f...:4ec...). Leave blank to keep the existing key."
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
            <Button 
              variant="outlined" 
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSaveChanges}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ManageUserApiKeys;
